<template>
  <div class="detail-container">
    <div class="page-header no-print">
      <div class="breadcrumb">ນັກຮຽນ / ການຍົກຍ້າຍໂຮງຮຽນ / <span>ປະຫວັດຄຳຮ້ອງ</span></div>
      <div class="header-content">
        <h1>ຄຳຮ້ອງຍົກຍ້າຍໂຮງຮຽນນັກຮຽນ</h1>
        <div style="display:flex;gap:10px;align-items:center;">
          <button class="btn-secondary" @click="$router.push('/home/students/transfer/list')">
            <i class="fas fa-arrow-left"></i> ກັບຄືນ
          </button>
          <button class="btn-primary" @click="doPrint" :disabled="loading || !transfer">
            <i class="fas fa-print"></i> ພິມແບບຟອມ
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="state-panel no-print"><i class="fas fa-spinner fa-spin"></i> ກຳລັງໂຫຼດຂໍ້ມູນ...</div>
    <div v-else-if="!transfer" class="state-panel no-print">ບໍ່ພົບຄຳຮ້ອງ</div>

    <template v-else>
      <!-- == Action panel (ບໍ່ພິມ) == -->
      <div class="panel action-panel no-print mb-4">
        <div class="panel-body">
          <div class="status-row">
            <span class="badge" :class="badgeClass">{{ statusLabel }}</span>
            <span class="status-hint">{{ statusHint }}</span>
          </div>

          <div v-if="canEditDraft" class="action-buttons">
            <button class="btn-secondary" @click="$router.push(`/home/students/transfer/edit/${transfer._id}`)">
              <i class="fas fa-edit"></i> ແກ້ໄຂ / ອັບໂຫລດເອກະສານ
            </button>
            <button class="btn-primary" @click="handleSubmitRequest" :disabled="acting || !hasAttachments">
              <i class="fas fa-paper-plane"></i> ສົ່ງຄຳຮ້ອງ
            </button>
            <button class="btn-danger" @click="handleDeleteDraft" :disabled="acting">
              <i class="fas fa-trash"></i> ລຶບຮ່າງ
            </button>
          </div>
          <div v-if="canEditDraft && !hasAttachments" class="hint-text">
            <i class="fas fa-info-circle"></i> ຕ້ອງອັບໂຫລດເອກະສານແນບຢ່າງໜ້ອຍ 1 ໄຟລ໌ກ່ອນຈຶ່ງຈະສົ່ງຄຳຮ້ອງໄດ້
          </div>

          <div v-if="canActOnStage" class="action-buttons">
            <button class="btn-primary" @click="handleApprove" :disabled="acting">
              <i class="fas fa-check"></i> {{ approveLabel }}
            </button>
            <button class="btn-danger" @click="rejectModal.show = true" :disabled="acting">
              <i class="fas fa-times"></i> ປະຕິເສດ
            </button>
          </div>
        </div>
      </div>

      <div class="document-shell">
        <div class="official-document" ref="printArea">
          <div class="doc-letterhead">
            <div class="letterhead-text">
              <h2>ສາທາລະນະລັດ ປະຊາທິປະໄຕ ປະຊາຊົນລາວ</h2>
              <div class="motto">ສັນຕິພາບ ເອກະລາດ ປະຊາທິປະໄຕ ເອກະພາບ ວັດທະນະຖາວອນ</div>
              <div class="motto-rule"></div>
            </div>
          </div>
          <div class="doc-ministry">ກະຊວງສຶກສາທິການ ແລະ ກິລາ</div>

          <div class="doc-title-block">
            <h1>ໃບຄຳຮ້ອງຂໍຍົກຍ້າຍໂຮງຮຽນນັກຮຽນ</h1>
            <div class="doc-subtitle">DOMESTIC STUDENT TRANSFER REQUEST</div>
          </div>

          <div class="doc-meta-row">
            <span>ເລກທີຄຳຮ້ອງ: <b>{{ transfer._id }}</b></span>
            <span>ວັນທີສ້າງຄຳຮ້ອງ: <b>{{ formatDate(transfer.created_at) }}</b></span>
            <span>ພິມວັນທີ: <b>{{ todayFormatted }}</b></span>
          </div>

          <section class="doc-section">
            <h3 class="section-title">I. ຂໍ້ມູນນັກຮຽນ</h3>
            <div class="info-grid">
              <div class="info-item"><label>ຊື່ ແລະ ນາມສະກຸນ</label><span>{{ transfer.student_snapshot.firstNameLao }} {{ transfer.student_snapshot.familyNameLao }}</span></div>
              <div class="info-item"><label>ລະຫັດນັກຮຽນ</label><span>{{ transfer.student_snapshot.student_id || '-' }}</span></div>
              <div class="info-item"><label>ເພດ</label><span>{{ transfer.student_snapshot.gender || '-' }}</span></div>
              <div class="info-item"><label>ວັນເດືອນປີເກີດ</label><span>{{ formatDate(transfer.student_snapshot.dob) }}</span></div>
              <div class="info-item"><label>ຊັ້ນຮຽນປັດຈຸບັນ</label><span>{{ transfer.student_snapshot.Grade_Code || '-' }}</span></div>
              <div class="info-item"><label>ສົກຮຽນປັດຈຸບັນ</label><span>{{ transfer.student_snapshot.accd || '-' }}</span></div>
            </div>
          </section>

          <section class="doc-section">
            <h3 class="section-title">II. ໂຮງຮຽນຕົ້ນທາງ ແລະ ປາຍທາງ</h3>
            <div class="info-grid">
              <div class="info-item"><label>ໂຮງຮຽນຕົ້ນທາງ</label><span>{{ transfer.from_school_name || transfer.from_school_id }}</span></div>
              <div class="info-item"><label>ໂຮງຮຽນປາຍທາງ</label><span>{{ transfer.to_school_name || transfer.to_school_id }}</span></div>
              <div class="info-item"><label>ຊັ້ນຮຽນທີ່ຂໍເຂົ້າ</label><span>{{ transfer.target_grade || '-' }}</span></div>
              <div class="info-item full"><label>ເຫດຜົນການຍົກຍ້າຍ</label><span>{{ transfer.reason || '-' }}</span></div>
            </div>
          </section>

          <section class="doc-section doc-attachments">
            <h3 class="section-title">III. ເອກະສານແນບ ({{ (transfer.attachments || []).length }})</h3>
            <div v-if="!hasAttachments" class="empty-docs">ບໍ່ມີເອກະສານແນບ</div>
            <ul v-else class="doc-list">
              <li v-for="(doc, i) in transfer.attachments" :key="i" class="doc-item">
                <i class="fas" :class="fileIcon(doc.name)"></i>
                <span class="doc-name">{{ doc.name }}</span>
                <span class="doc-actions no-print">
                  <button type="button" @click="previewDoc(doc)"><i class="fas fa-eye"></i> ເບິ່ງ</button>
                  <a :href="doc.data" :download="doc.name"><i class="fas fa-download"></i> ດາວໂຫລດ</a>
                </span>
              </li>
            </ul>
          </section>

          <section class="doc-section">
            <h3 class="section-title">IV. ຂັ້ນຕອນອະນຸມັດ</h3>
            <table class="approval-table">
              <thead>
                <tr>
                  <th>ຂັ້ນຕອນ</th>
                  <th>ຜູ້ດຳເນີນການ</th>
                  <th>ຜົນ</th>
                  <th>ໝາຍເຫດ</th>
                  <th>ວັນທີ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(step, i) in approvalSteps" :key="i">
                  <td class="text-left">{{ step.label }}</td>
                  <td>{{ step.entry ? step.entry.by_name : '—' }}</td>
                  <td>{{ step.entry ? actionLabel(step.entry.action) : 'ລໍຖ້າ' }}</td>
                  <td class="text-left">{{ step.entry ? (step.entry.note || '-') : '-' }}</td>
                  <td>{{ step.entry ? formatDateTime(step.entry.at) : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <div class="doc-footer-note">ເອກະສານສະບັບນີ້ພິມຈາກລະບົບຄຸ້ມຄອງຂໍ້ມູນນັກຮຽນ (SLFIS-KMS) ວັນທີ {{ todayFormatted }}</div>
        </div>
      </div>
    </template>

    <!-- Modal ປະຕິເສດ -->
    <transition name="modal">
      <div v-if="rejectModal.show" class="modal-overlay no-print" @mousedown.self="rejectModal.show = false">
        <div class="custom-modal-card">
          <h3>ປະຕິເສດຄຳຮ້ອງ</h3>
          <p class="hint-text">ກະລຸນາລະບຸເຫດຜົນການປະຕິເສດ — ຜູ້ສ້າງຄຳຮ້ອງຈະໄດ້ຮັບການແຈ້ງເຕືອນ</p>
          <textarea v-model="rejectModal.note" rows="4" placeholder="ເຫດຜົນການປະຕິເສດ..."></textarea>
          <div class="modal-footer-actions">
            <button class="btn-secondary" @click="rejectModal.show = false">ຍົກເລີກ</button>
            <button class="btn-danger" @click="handleReject" :disabled="!rejectModal.note || acting">ຢືນຢັນປະຕິເສດ</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Modal ເບິ່ງເອກະສານ -->
    <transition name="modal">
      <div v-if="previewModal.show" class="modal-overlay no-print" @mousedown.self="closePreview">
        <div class="preview-modal-card">
          <div class="preview-header">
            <h3>{{ previewModal.name }}</h3>
            <button type="button" class="close-btn" @click="closePreview">&times;</button>
          </div>
          <div class="preview-body">
            <img v-if="previewModal.isImage" :src="previewModal.data" :alt="previewModal.name">
            <iframe v-else-if="previewModal.isPdf" :src="previewModal.data" title="preview"></iframe>
            <div v-else class="no-preview">
              <i class="fas fa-file-alt"></i>
              <p>ບໍ່ສາມາດສະແດງຕົວຢ່າງໄຟລ໌ປະເພດນີ້ໄດ້</p>
              <a :href="previewModal.data" :download="previewModal.name" class="btn-primary">ດາວໂຫລດເອກະສານ</a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';
import { transferStatusLabel, transferStatusBadgeClass } from '@/utils/studentTransferStatus';

export default {
  name: 'StudentTransferDetailPage',
  props: { id: { type: String, default: null } },
  data() {
    return {
      loading: true,
      acting: false,
      transfer: null,
      user: {},
      rejectModal: { show: false, note: '' },
      previewModal: { show: false, name: '', data: '', isImage: false, isPdf: false }
    };
  },
  computed: {
    statusLabel() { return this.transfer ? transferStatusLabel(this.transfer.status) : ''; },
    badgeClass() { return this.transfer ? transferStatusBadgeClass(this.transfer.status) : ''; },
    hasAttachments() { return !!(this.transfer && this.transfer.attachments && this.transfer.attachments.length); },
    todayFormatted() {
      return new Date().toLocaleDateString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    isAdmin() { return parseInt(this.user.user_level) === 1; },
    canEditDraft() {
      if (!this.transfer) return false;
      return this.transfer.status === 'Draft' && (this.isAdmin || this.transfer.created_by === this.user.username);
    },
    canActOnStage() {
      if (!this.transfer) return false;
      const t = this.transfer;
      if (t.status === 'PendingHomePESS') {
        return this.isAdmin || (this.user.org_role === 'pess' && this.user.prov_id === t.from_prov_id);
      }
      if (t.status === 'PendingHostPESS') {
        return this.isAdmin || (this.user.org_role === 'pess' && this.user.prov_id === t.to_prov_id);
      }
      if (t.status === 'PendingHostSchool') {
        return this.isAdmin || (this.user.org_role === 'school' && this.user.school_id === t.to_school_id);
      }
      return false;
    },
    approveLabel() {
      if (!this.transfer) return '';
      if (this.transfer.status === 'PendingHostSchool') return 'ຮັບຮອງ';
      return 'ອະນຸມັດ';
    },
    statusHint() {
      const hints = {
        Draft: 'ຍັງບໍ່ໄດ້ສົ່ງຄຳຮ້ອງ — ອັບໂຫລດເອກະສານແລ້ວກົດ "ສົ່ງຄຳຮ້ອງ"',
        PendingHomePESS: 'ລໍຖ້າພະແນກສຶກສາແຂວງຕົ້ນທາງກວດສອບ',
        PendingHostPESS: 'ລໍຖ້າພະແນກສຶກສາແຂວງປາຍທາງອະນຸມັດ',
        PendingHostSchool: 'ລໍຖ້າໂຮງຮຽນປາຍທາງຮັບຮອງ',
        Accepted: 'ຂໍ້ມູນນັກຮຽນຖືກຍົກຍ້າຍໄປໂຮງຮຽນປາຍທາງແລ້ວ'
      };
      return hints[this.transfer.status] || '';
    },
    approvalSteps() {
      if (!this.transfer) return [];
      const timeline = this.transfer.timeline || [];
      const findEntry = (stage) => timeline.find(t => t.stage === stage);
      return [
        { label: 'I. Home DESB ສ້າງຄຳຮ້ອງ', entry: timeline[0] },
        { label: 'II. Home PESS ກວດສອບ', entry: findEntry('PendingHostPESS') || findEntry('RejectedByHomePESS') },
        { label: 'III. Host PESS ອະນຸມັດ', entry: findEntry('PendingHostSchool') || findEntry('RejectedByHostPESS') },
        { label: 'IV. Host School ຮັບຮອງ', entry: findEntry('Accepted') || findEntry('RejectedByHostSchool') }
      ];
    }
  },
  mounted() {
    this.loadUserInfo();
    this.fetchTransfer();
  },
  methods: {
    loadUserInfo() {
      const savedInfo = localStorage.getItem('user-info');
      if (savedInfo) {
        try { this.user = JSON.parse(savedInfo); } catch (e) { this.user = {}; }
      }
    },
    async fetchTransfer() {
      const transferId = this.id || this.$route.params.id;
      this.loading = true;
      try {
        const res = await api.get(`/get/student_transfers/${transferId}`);
        this.transfer = (res.data.code === 1 && res.data.rows) ? res.data.rows : null;
      } catch (e) {
        console.error('fetchTransfer error:', e);
        this.transfer = null;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('lo-LA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    actionLabel(action) {
      const labels = { created: 'ສ້າງຄຳຮ້ອງ', submitted: 'ສົ່ງຄຳຮ້ອງ', approve: 'ອະນຸມັດ', accept: 'ຮັບຮອງ', reject: 'ປະຕິເສດ' };
      return labels[action] || action;
    },
    fileIcon(name) {
      const ext = (name || '').split('.').pop().toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fa-file-image';
      if (ext === 'pdf') return 'fa-file-pdf';
      if (['doc', 'docx'].includes(ext)) return 'fa-file-word';
      return 'fa-file-alt';
    },
    previewDoc(doc) {
      const mime = (doc.data || '').substring(5, (doc.data || '').indexOf(';'));
      this.previewModal = { show: true, name: doc.name, data: doc.data, isImage: mime.startsWith('image/'), isPdf: mime === 'application/pdf' };
    },
    closePreview() { this.previewModal.show = false; },
    doPrint() { window.print(); },
    async handleSubmitRequest() {
      this.acting = true;
      try {
        const res = await api.post(`/student-transfers/${this.transfer._id}/submit`, { by_name: this.user.fullname || this.user.username });
        if (res.data.code === 1) await this.fetchTransfer();
        else alert(res.data.msg || 'ບໍ່ສາມາດສົ່ງຄຳຮ້ອງໄດ້');
      } catch (e) {
        alert((e.response && e.response.data && e.response.data.msg) || 'ເກີດຂໍ້ຜິດພາດໃນການສົ່ງຄຳຮ້ອງ');
      } finally {
        this.acting = false;
      }
    },
    async handleDeleteDraft() {
      if (!confirm('ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຄຳຮ້ອງຮ່າງນີ້?')) return;
      this.acting = true;
      try {
        await api.delete(`/del/student_transfers/${this.transfer._id}`);
        this.$router.push('/home/students/transfer/list');
      } catch (e) {
        alert((e.response && e.response.data && e.response.data.msg) || 'ເກີດຂໍ້ຜິດພາດໃນການລຶບ');
      } finally {
        this.acting = false;
      }
    },
    endpointForStage() {
      if (this.transfer.status === 'PendingHomePESS') return 'review';
      if (this.transfer.status === 'PendingHostPESS') return 'approve';
      if (this.transfer.status === 'PendingHostSchool') return 'accept';
      return null;
    },
    async handleApprove() {
      const endpoint = this.endpointForStage();
      if (!endpoint) return;
      this.acting = true;
      try {
        const decision = endpoint === 'accept' ? 'accept' : 'approve';
        const res = await api.post(`/student-transfers/${this.transfer._id}/${endpoint}`, {
          decision, by_name: this.user.fullname || this.user.username
        });
        if (res.data.code === 1) await this.fetchTransfer();
        else alert(res.data.msg || 'ບໍ່ສາມາດດຳເນີນການໄດ້');
      } catch (e) {
        alert((e.response && e.response.data && e.response.data.msg) || 'ເກີດຂໍ້ຜິດພາດ');
      } finally {
        this.acting = false;
      }
    },
    async handleReject() {
      const endpoint = this.endpointForStage();
      if (!endpoint || !this.rejectModal.note) return;
      this.acting = true;
      try {
        const res = await api.post(`/student-transfers/${this.transfer._id}/${endpoint}`, {
          decision: 'reject', note: this.rejectModal.note, by_name: this.user.fullname || this.user.username
        });
        if (res.data.code === 1) {
          this.rejectModal = { show: false, note: '' };
          await this.fetchTransfer();
        } else alert(res.data.msg || 'ບໍ່ສາມາດປະຕິເສດໄດ້');
      } catch (e) {
        alert((e.response && e.response.data && e.response.data.msg) || 'ເກີດຂໍ້ຜິດພາດ');
      } finally {
        this.acting = false;
      }
    }
  }
};
</script>

<style scoped>
.detail-container { animation: fadeIn 0.4s ease; }
.header-content { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
.btn-primary, .btn-secondary, .btn-danger {
  border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;
  font-weight: 500; display: flex; align-items: center; gap: 8px; font-family: inherit; font-size: 13px;
}
.btn-primary { background: var(--lao); color: #fff; }
.btn-primary:hover { background: var(--lao-mid); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: var(--text2); border: 1px solid var(--border); }
.btn-secondary:hover { background: #f4f5f7; }
.btn-danger { background: var(--danger); color: #fff; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

.state-panel { background: var(--card); border: 1px solid var(--border); border-radius: 10px; padding: 60px 20px; text-align: center; color: var(--text3); margin-top: 16px; }

.action-panel .status-row { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.badge { font-size: 12px; padding: 5px 12px; border-radius: 6px; font-weight: 700; }
.badge-success { background: var(--success-bg); color: var(--success); }
.badge-warning { background: var(--warning-bg); color: var(--warning); }
.badge-danger { background: var(--danger-bg); color: var(--danger); }
.badge-neutral { background: var(--surface); color: var(--text2); }
.status-hint { font-size: 12.5px; color: var(--text3); }
.action-buttons { display: flex; gap: 10px; margin-top: 10px; }
.hint-text { font-size: 12px; color: var(--text3); margin-top: 8px; }

.mb-4 { margin-bottom: 16px; }
.document-shell { display: flex; justify-content: center; }

.official-document {
  background: #fff; width: 100%; max-width: 860px; padding: 40px 48px;
  border: 1px solid var(--border); box-shadow: 0 2px 12px rgba(0,0,0,0.06); border-radius: 4px;
}
.doc-letterhead { display: flex; align-items: center; justify-content: center; text-align: center; }
.letterhead-text h2 { font-size: 16px; font-weight: 700; color: var(--text1); }
.letterhead-text .motto { font-size: 12.5px; color: var(--text2); margin-top: 2px; }
.motto-rule { width: 140px; height: 1px; background: var(--text2); margin: 4px auto 0; }
.doc-ministry { text-align: center; font-size: 13.5px; font-weight: 600; color: var(--lao); margin-top: 10px; }

.doc-title-block { text-align: center; margin: 22px 0 10px; }
.doc-title-block h1 { font-size: 20px; font-weight: 700; color: var(--text1); letter-spacing: 0.02em; }
.doc-subtitle { font-size: 11px; letter-spacing: 0.12em; color: var(--gold); font-weight: 600; margin-top: 4px; }

.doc-meta-row {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 6px; font-size: 12px; color: var(--text2);
  border-top: 2px solid var(--gold); border-bottom: 1px solid var(--border);
  padding: 8px 2px; margin-bottom: 20px;
}

.doc-section { margin-bottom: 18px; }
.section-title {
  font-size: 13px; font-weight: 700; color: #fff; background: var(--lao);
  padding: 5px 10px; border-radius: 3px; margin-bottom: 8px;
}
.info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
.info-item { display: flex; flex-direction: column; font-size: 12.5px; border-bottom: 1px dotted var(--border); padding-bottom: 4px; }
.info-item.full { grid-column: 1 / -1; }
.info-item label { color: var(--text3); font-size: 11px; margin-bottom: 2px; }
.info-item span { color: var(--text1); font-weight: 500; }

.empty-docs { font-size: 12.5px; color: var(--text3); padding: 8px 0; }
.doc-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.doc-item {
  display: flex; align-items: center; gap: 10px; font-size: 12.5px;
  padding: 8px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--surface);
}
.doc-item i.fa-file-pdf { color: var(--danger); }
.doc-item i.fa-file-image { color: var(--lao-mid); }
.doc-item i.fa-file-word { color: #2b579a; }
.doc-name { flex: 1; word-break: break-all; }
.doc-actions { display: flex; gap: 6px; }
.doc-actions button, .doc-actions a {
  border: 1px solid var(--border); background: #fff; color: var(--lao); font-size: 11px;
  padding: 4px 8px; border-radius: 4px; cursor: pointer; text-decoration: none; font-family: inherit;
}

.approval-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.approval-table th, .approval-table td { border: 1px solid var(--border); padding: 8px; text-align: center; }
.approval-table th { background: #f8fafc; color: #475569; font-weight: 700; }
.text-left { text-align: left; }

.doc-footer-note { margin-top: 24px; text-align: center; font-size: 10.5px; color: var(--text3); }

.modal-overlay {
  position: fixed; inset: 0; background: rgba(13,27,42,0.55); display: flex;
  align-items: center; justify-content: center; z-index: 1000; padding: 20px;
}
.custom-modal-card { background: #fff; border-radius: 12px; max-width: 420px; width: 100%; padding: 24px; }
.custom-modal-card h3 { font-size: 15px; margin-bottom: 8px; color: var(--text1); }
.custom-modal-card textarea { width: 100%; padding: 10px; border: 1px solid var(--border); border-radius: 8px; font-family: inherit; font-size: 13px; margin-top: 8px; }
.modal-footer-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

.preview-modal-card {
  background: #fff; border-radius: 10px; width: 100%; max-width: 720px; max-height: 88vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.preview-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid var(--border); }
.preview-header h3 { font-size: 14px; color: var(--text1); word-break: break-all; }
.close-btn { border: none; background: none; font-size: 22px; cursor: pointer; color: var(--text3); line-height: 1; }
.preview-body { padding: 16px; overflow: auto; display: flex; align-items: center; justify-content: center; flex: 1; background: var(--surface); }
.preview-body img { max-width: 100%; max-height: 70vh; object-fit: contain; }
.preview-body iframe { width: 100%; height: 70vh; border: none; background: #fff; }
.no-preview { text-align: center; color: var(--text2); }
.no-preview i { font-size: 40px; color: var(--text3); margin-bottom: 10px; }
.no-preview p { margin: 8px 0 14px; }

@media print {
  .no-print { display: none !important; }
  body * { visibility: hidden; }
  .official-document, .official-document * { visibility: visible; }
  .official-document {
    position: absolute; top: 0; left: 0; width: 100%; max-width: 100%;
    box-shadow: none; border: none; padding: 10mm;
  }
  .doc-actions { display: none !important; }
}

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>
